import { proxy } from 'valtio';
import { devtools } from 'valtio/utils';

import getJob from '@cartwhl/common/api/getJob';
import getJobs from '@cartwhl/common/api/getJobs';
import postBrowseSearch from '@cartwhl/common/api/postBrowseSearch';
import { BrowseResultItem, CategoryAggregation } from '@cartwhl/common/types';
import handleGenericErrors from '@cartwhl/common/utils/handleGenericErrors';

import { DEFAULT_BROWSE_CATEGORIES } from 'components/BrowseMotions/defaultCategories';
import { noticesStore } from 'stores';

// Constants for localStorage
const REVIEW_MODE_STORAGE_KEY = 'cartwheel_browse_review_mode';

export const IMAGE_PATH_BY_CATEGORY: Record<string, string> = {
  'action and adventure': '/images/browse_thumbnails/action_adventure.webp',
  'character interaction':
    '/images/browse_thumbnails/character_interaction.webp',
  characters: '/images/browse_thumbnails/character.webp',
  combat: '/images/browse_thumbnails/combat.webp',
  dance: '/images/browse_thumbnails/dance.webp',
  'environment interaction':
    '/images/browse_thumbnails/environment_interaction.webp',
  everyday: '/images/browse_thumbnails/everyday.webp',
  idle: '/images/browse_thumbnails/idle.webp',
  locomotion: '/images/browse_thumbnails/locomotion.webp',
  music: '/images/browse_thumbnails/music.webp',
  'reactions and outbursts':
    '/images/browse_thumbnails/reactions_outbursts.webp',
  sports: '/images/browse_thumbnails/sports.webp',
  superpower: '/images/browse_thumbnails/superpower.webp',
  tools: '/images/browse_thumbnails/tools.webp',
  'vehicles and transportation':
    '/images/browse_thumbnails/transportation.webp',
};

export const MOTIONS_BY_CATEGORY: Record<string, number> = {
  'action and adventure': 15740,
  'character interaction': 2668,
  characters: 358,
  combat: 6211,
  dance: 3203,
  'environment interaction': 4720,
  everyday: 28211,
  idle: 9839,
  locomotion: 21370,
  music: 318,
  'reactions and outbursts': 10240,
  'real people': 78,
  sports: 4438,
  superpower: 470,
  tools: 1522,
  'vehicles and transportation': 103,
};

const URL_EXPIRATION_TIME = 600; // 10 minutes in seconds

export const BROWSE_RESULTS_FETCH_SIZE = 12;

// Constants for different horizontal page sizes
export const BROWSE_VIEWPORT_GRID_SIZES = {
  LARGE_VIEW: 35,
  MEDIUM_VIEW: 30,
  DEFAULT: 32,
} as const;

// Get the appropriate page size
export function getBrowseViewPageSize(columns: number): number {
  switch (columns) {
    case 7:
      return BROWSE_VIEWPORT_GRID_SIZES.LARGE_VIEW;
    case 3:
    case 5:
    case 6:
      return BROWSE_VIEWPORT_GRID_SIZES.MEDIUM_VIEW;
    default:
      return BROWSE_VIEWPORT_GRID_SIZES.DEFAULT;
  }
}

export const BROWSE_VIEW_REDUCED_PAGE_SIZE = 12;

type CachedBvhUrl = {
  url: string;
  fetchedAt: number;
};

type BrowseMotionPage = {
  items: BrowseResultItem[];
  lastSort?: [number, string];
};

type BrowseMotionStoreState = {
  availableCategoryInformation: CategoryAggregation[];
  bvhUrlCache: Record<string, CachedBvhUrl>;
  currentCategory: string;
  currentSearchTerm: string;
  currentSubcategory: string;
  favoritePages: BrowseMotionPage[];
  favoriteRecords: BrowseResultItem[];
  hasMore: boolean;
  loading: {
    animation: boolean;
    data: boolean;
    init: boolean;
    nextPage: boolean;
  };
  pages: BrowseMotionPage[];
  total: number;
  reviewMode: boolean;
  selectedView: 'library' | 'favorites';
  suggestions: {
    searchTerm: string;
    data: BrowseResultItem[];
    loading: boolean;
    total: number;
  };
};

// Load review mode from localStorage if available
const loadReviewModeFromStorage = (): boolean => {
  if (typeof window === 'undefined') return false;

  try {
    const storedValue = localStorage.getItem(REVIEW_MODE_STORAGE_KEY);
    return storedValue === 'true';
  } catch (error) {
    console.error('Error loading review mode from localStorage:', error);
    return false;
  }
};

export const state = proxy<BrowseMotionStoreState>({
  availableCategoryInformation: DEFAULT_BROWSE_CATEGORIES,
  bvhUrlCache: {},
  currentCategory: '',
  currentSearchTerm: '',
  currentSubcategory: '',
  favoritePages: [],
  favoriteRecords: [],
  hasMore: false,
  loading: {
    animation: false,
    data: false,
    init: false,
    nextPage: false,
  },
  pages: [],
  total: 0,
  reviewMode: loadReviewModeFromStorage(), // Initialize from localStorage
  selectedView: 'library',
  suggestions: {
    searchTerm: '',
    data: [],
    loading: false,
    total: 0,
  },
});

if (process.env.NODE_ENV === 'development') {
  devtools(state, { name: 'BrowseMotion', enabled: true });
}

function isUrlExpired(fetchedAt: number): boolean {
  const now = Math.floor(Date.now() / 1000); // Convert to seconds
  return now - fetchedAt > URL_EXPIRATION_TIME;
}

function getCachedUrl(jobID: string): string | null {
  const cached = state.bvhUrlCache[jobID];
  if (!cached) return null;
  if (isUrlExpired(cached.fetchedAt)) {
    delete state.bvhUrlCache[jobID];
    return null;
  }
  return cached.url;
}

function cacheUrl(jobID: string, url: string) {
  state.bvhUrlCache[jobID] = {
    url,
    fetchedAt: Math.floor(Date.now() / 1000),
  };
}

export async function setLoading(key: 'data' | 'animation', loading: boolean) {
  state.loading[key] = loading;
}

export async function initSearchSuggestions(
  params: {
    searchTerm: string;
  },
  signal?: AbortSignal,
) {
  try {
    state.suggestions.loading = true;
    state.suggestions.data = [];
    state.suggestions.searchTerm = params.searchTerm;

    const { data, error } = await postBrowseSearch(
      {
        pageSize: BROWSE_RESULTS_FETCH_SIZE,
        ...params,
      },
      signal,
    );

    if (error) {
      throw error;
    }

    if (data) {
      state.suggestions.data = data.items.slice(
        0,
        BROWSE_VIEW_REDUCED_PAGE_SIZE,
      );
      state.suggestions.total = data.total ?? 0;
    }
  } catch (error) {
    if ((error as Error).message.includes('aborted')) {
      console.log('Search suggestions request was aborted by user');
    } else {
      console.error(`Failed to load search suggestion results: ${error}`);
      noticesStore.error('Failed to load results');
    }
  } finally {
    state.suggestions.loading = false;
  }
}

export async function initSearch(
  params: {
    searchTerm: string;
    category: string;
    subcategory: string;
  },
  signal?: AbortSignal,
) {
  if (state.selectedView === 'favorites') {
    await fetchFavorites(true);
    return;
  }
  state.loading.data = true;
  state.pages = [];
  state.currentCategory = params.category;
  state.currentSubcategory = params.subcategory;

  try {
    const { data, error } = await postBrowseSearch(
      {
        pageSize: BROWSE_RESULTS_FETCH_SIZE,
        ...params,
        // review: state.reviewMode,
      },
      signal,
    );

    if (error) {
      throw error;
    }

    if (data) {
      if (
        params.searchTerm &&
        params.category === '' &&
        params.subcategory === '' &&
        data.items.length > 0
      ) {
        state.availableCategoryInformation = data.categoryInformation;
      } else {
        state.availableCategoryInformation = DEFAULT_BROWSE_CATEGORIES;
      }

      state.pages = [
        {
          items: data.items,
          lastSort: data.lastSort,
        },
      ];

      state.hasMore = data.hasMore ?? false;
      state.total = data.total ?? 0;
    }
  } catch (error) {
    if ((error as Error).message.includes('aborted')) {
      console.log('Browse search request was aborted by user');
    } else {
      console.error(`Failed to load browse search results: ${error}`);
      noticesStore.error('Failed to load results');
    }
  } finally {
    state.loading.data = false;
  }
}

export async function loadNextPage() {
  if (!state.hasMore || state.loading.nextPage || state.loading.data) return;
  state.loading.nextPage = true;
  const lastPage = state.pages[state.pages.length - 1];

  try {
    const { data, error } = await postBrowseSearch({
      pageSize: BROWSE_RESULTS_FETCH_SIZE,
      searchTerm: state.currentSearchTerm,
      category: state.currentCategory,
      subcategory: state.currentSubcategory,
      searchAfter: lastPage?.lastSort,
      review: state.reviewMode,
    });

    if (error) {
      noticesStore.error('Failed to load more results');
      return;
    }

    if (data) {
      state.pages.push({
        items: data.items,
        lastSort: data.lastSort,
      });
      state.hasMore = data.hasMore ?? false;
      state.total = data.total ?? 0;
    }
  } catch (error) {
    noticesStore.error('Failed to load more results');
  } finally {
    state.loading.nextPage = false;
  }
}

export async function fetchBvhFromJob(jobID: string) {
  const cachedUrl = getCachedUrl(jobID);
  if (cachedUrl) return cachedUrl;

  state.loading.animation = true;

  const { data, error } = await getJob(jobID);

  if (error) {
    const params = encodeURIComponent(encodeURIComponent(`jobID=${jobID}`));
    await handleGenericErrors(error, { redirectTo: `studio/home?${params}` });
    state.loading.animation = false;
  } else if (data) {
    const variation = data.variationInfoByID[0];
    if (variation.bvhURL) {
      cacheUrl(jobID, variation.bvhURL);
      return variation.bvhURL;
    } else {
      noticesStore.error(`Motion could not be found. Please try again`);
    }
  }
  state.loading.animation = false;
}

export async function fetchFavorites(skipJobFetch: boolean) {
  state.loading.data = true;
  state.favoritePages = [];

  try {
    if (!skipJobFetch) {
      const { data, error } = await getJobs(1000, undefined, false, true);

      if (error) {
        throw error;
      }

      if (data?.jobs) {
        // Convert jobs to BrowseResultItems shape
        const favoriteItems: BrowseResultItem[] = data.jobs.flatMap((job) =>
          Object.entries(job.variationInfoByID).map(([_, variation]) => ({
            jobID: job.jobID,
            title: job.sessionName || 'Untitled',
            short: '',
            tags: [],
            favorite: true,
            created: job.createdAt,
            categoryAndSubcategories: ['Favorites.All Favorites'],
            mp4Path: variation.mp4Path || '',
            mp4PathV2: variation.mp4Path || '',
            gifPathV2: '',
          })),
        );

        state.favoriteRecords = favoriteItems;

        // Structure the favorites data like regular browse pages
        state.favoritePages = [
          {
            items: state.favoriteRecords,
            lastSort: undefined,
          },
        ];
      }
    }
    state.favoritePages = [
      {
        items: state.favoriteRecords.filter((item) =>
          state.currentSearchTerm
            ? item.title
                .toLowerCase()
                .includes(state.currentSearchTerm.toLowerCase())
            : true,
        ),
        lastSort: undefined,
      },
    ];
    state.hasMore = false;
    state.total = state.favoritePages[0].items.length;

    // Set category information for favorites view
    state.availableCategoryInformation = [
      {
        category: 'Favorites',
        doc_count: state.favoritePages[0].items.length,
        subcategories: [
          {
            subcategory: 'All Favorites',
            doc_count: state.favoritePages[0].items.length,
            tags: [],
          },
        ],
      },
    ];
  } catch (error) {
    noticesStore.error('Failed to load favorites');
  } finally {
    state.loading.data = false;
  }
}

export function setCurrentSearchTerm(searchTerm: string) {
  state.currentSearchTerm = searchTerm;
}

export function setSelectedView(view: 'library' | 'favorites') {
  state.selectedView = view;
  if (view === 'favorites') {
    state.currentCategory = 'Favorites';
    state.currentSubcategory = 'All Favorites';
    state.currentSearchTerm = '';
    fetchFavorites(false);
  }
}

export function setReviewMode(enabled: boolean) {
  // If the review mode hasn't changed, don't do anything
  if (state.reviewMode === enabled) {
    return;
  }

  state.reviewMode = enabled;

  // Save to localStorage
  try {
    localStorage.setItem(REVIEW_MODE_STORAGE_KEY, String(enabled));
  } catch (error) {
    console.error('Error saving review mode to localStorage:', error);
  }

  // Only re-initialize search if we're not already loading data
  if (!state.loading.data && !state.loading.nextPage) {
    initSearch({
      searchTerm: state.currentSearchTerm,
      category: state.currentCategory,
      subcategory: state.currentSubcategory,
    });
  }
}

/**
 * Remove an item from the browse results by jobID
 * Used when submitting feedback to immediately remove the item from the UI
 */
export function removeItemFromResults(jobID: string) {
  // Remove from pages
  state.pages = state.pages.map((page) => ({
    ...page,
    items: page.items.filter((item) => item.jobID !== jobID),
  }));

  // Remove from favorites if present
  state.favoritePages = state.favoritePages.map((page) => ({
    ...page,
    items: page.items.filter((item) => item.jobID !== jobID),
  }));

  state.favoriteRecords = state.favoriteRecords.filter(
    (item) => item.jobID !== jobID,
  );
}
